<template>
	<div class="pagination">
		<nav class="pagination-nav" aria-label="Pagination Navigation">
			<button
				class="previous-button"
				aria-label="Previous Page"
				:disabled="currentPageNumber === 1"
				@click="handlePrevious"
			>
				<font-awesome-icon class="left-icon" :icon="['far', 'chevron-left']" />
			</button>
			<button
				class="primary-button"
				:aria-label="`Page Number ${page}`"
				v-for="page in pages"
				:class="{ active: currentPageNumber === page }"
				:key="page"
				@click="handlePageClick(page)"
			>
				{{ page }}
			</button>
			<button
				class="next-button"
				aria-label="Next Page"
				@click="handleNext"
				:disabled="currentPageNumber === numberOfPages"
			>
				<font-awesome-icon
					class="right-icon"
					:icon="['far', 'chevron-right']"
				/>
			</button>
		</nav>
		<FormDropdowns
			class="page-size-dropdown"
			v-model="currentPageSize"
			:options="pageSizeOptions"
			@input="handlePageSizeChange"
		/>
		<div class="results">{{ currentPageResults }} of {{ totalCount }}</div>
	</div>
</template>

<script>
import FormDropdowns from './FormDropdowns.vue';

export default {
	name: 'Pagination',
	props: {
		pageNumber: {
			type: Number,
			default: 1,
		},
		pageSize: {
			type: Number,
			default: 20,
		},
		totalCount: Number,
	},
	components: { FormDropdowns },
	computed: {
		currentPageResults() {
			let rangeEnd = this.currentPageNumber * this.currentPageSize;
			if (rangeEnd > this.totalCount) {
				rangeEnd = this.totalCount;
			}
			return `${
				this.currentPageNumber * this.currentPageSize - this.currentPageSize + 1
			} - ${rangeEnd}`;
		},
		numberOfPages() {
			return Math.ceil(this.totalCount / this.pageSize)
		}
	},
	data() {
		return {
			currentPageNumber: null,
			currentPageSize: null,
			pages: [],
			pageSizeOptions: [
				{
					name: '5 results',
					value: 5,
					id: 1,
				},
				{
					name: '10 results',
					value: 10,
					id: 2,
				},
				{
					name: '20 results',
					value: 20,
					id: 3,
				},
			],
		};
	},
	methods: {
		handleNext() {
            if (this.currentPageNumber + 1 > this.numberOfPages) {
                return;
            }
            this.currentPageNumber = this.currentPageNumber + 1;
            if (!this.pages.includes(this.currentPageNumber)) {
                this.buildPages(false);
            }
            this.$emit('onPageChange', this.currentPageNumber);
		},
		handlePrevious() {
			if (this.currentPageNumber - 1 < 1) {
				return;
			}
			this.currentPageNumber = this.currentPageNumber - 1;
			if (!this.pages.includes(this.currentPageNumber)) {
				this.buildPages(true);
			}
			this.$emit('onPageChange', this.currentPageNumber);
		},
		handlePageClick(page) {
			this.currentPageNumber = page;
			this.$emit('onPageChange', this.currentPageNumber);
		},
		handlePageSizeChange() {
			this.$emit('onPageSizeChange', this.currentPageSize);
		},
		buildPages(isPreviousPage) {
			this.pages = [];
			let startingPage, endingPage;

			if (isPreviousPage) {
				startingPage = Math.max(1, this.currentPageNumber - 3);
				endingPage = Math.min(startingPage + 3, this.numberOfPages);
			}
			else {
				endingPage = Math.min(this.currentPageNumber + 3, this.numberOfPages);
				startingPage = Math.max(endingPage - 3, 1);
			}
			for (
				let i = startingPage;
				i <= endingPage;
				i++
			) {
				this.pages.push(i);
			}
		},
	},
	mounted() {
		this.currentPageNumber = this.pageNumber;
		this.currentPageSize = this.pageSize;
		this.buildPages();
	},
	watch: {
		pageNumber(newPageNumber) {
			if (this.currentPageNumber !== newPageNumber) {
				this.currentPageNumber = newPageNumber;
				this.buildPages();
			}
		},
		currentPageSize(newSize, oldSize) {
			if (oldSize !== null && newSize !== oldSize) {
				this.buildPages();
			}
		},
		numberOfPages(newNumber, oldNumber) {
			if (oldNumber !== newNumber) {
				this.buildPages();
			}
		}
	},
};
</script>

<style lang="scss" scoped>
.pagination {
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	align-items: center;
	border: 1px solid $border-color-light;
	border-top: none;
	border-radius: 0 0 6px 6px;
	width: 100%;

	@media only screen and (min-width: 601px) {
		flex-direction: row;
		align-items: center;
		justify-content: space-between;

		.pagination-nav {
			display: flex;
			.previous-button,
			.next-button,
			.primary-button {
				width: 34px;
			}
		}

		.page-size-dropdown {
			order: 3;
			@include rem('margin', 15px);
			max-width: 184px;

			& /deep/ .b360-form-input--select {
				max-width: 184px;
			}
		}

		.results {
			@include rem('margin', 15px);
		}
	}
}

.pagination-nav {
	display: grid;
	grid-template-columns: repeat(6, 1fr);
	@include rem('padding', 15px 0);
	@include rem('margin', 0 15px);

	.previous-button,
	.next-button,
	.primary-button {
		height: 44px;
		width: 44px;
		background: white;
		font-weight: bold;
		color: $brand-blue;
		border: 1px solid $border-color-light;
		cursor: pointer;

		&:focus {
			outline: none;
		}

		&:disabled {
			color: $b360-color--gray__lighter;
		}
	}

	.primary-button {
		&.active {
			background: $highlight-color;
		}
	}

	.next-button {
		border-left: none;
		border-radius: 0 6px 6px 0;
	}

	.primary-button,
	.next-button {
		border-left: none;
	}

	.previous-button {
		border-radius: 6px 0 0 6px;
	}
}

.page-size-dropdown {
	background-color: $white;
	@include rem('margin', 0 15px 16px 15px);
	width: 264px;
}

.results {
	text-align: center;
	@include rem('margin-bottom', 15px);
}
</style>
